import React, { useMemo } from 'react';
import { View, Text, StyleSheet, TextProps } from 'react-native';
import { parseAllReferences, type ParsedReference } from '../utils/chatUtils';
import DataReferenceView from './DataReferenceView';

interface ParsedTextWithReferencesProps {
  text: string;
  textStyle?: TextProps['style'];
  onReferencePress?: (reference: ParsedReference) => void;
  theme?: boolean;
}

/**
 * Component that parses text and renders data references as bordered, interactive views
 * Performance optimized with memoization
 */
const ParsedTextWithReferences: React.FC<ParsedTextWithReferencesProps> = React.memo(({
  text,
  textStyle,
  onReferencePress,
  theme: themeProp,
}) => {
  // Normalize text first to ensure it's always a string (not undefined, not an array)
  let normalizedText: string;
  if (Array.isArray(text)) {
    console.warn('⚠️ ParsedTextWithReferences - text prop is an array, joining:', text);
    normalizedText = text.join('');
  } else if (text != null && typeof text !== 'undefined') {
    normalizedText = String(text);
  } else {
    normalizedText = '';
  }

  // Use theme prop directly, default to true if not provided
  const theme = themeProp !== undefined ? themeProp : true;

  // Memoize parsing to avoid re-parsing on every render
  // normalizedText is always a string, so the dependency is always defined
  const segments = useMemo(() => {
    if (!normalizedText || normalizedText.length === 0) {
      return [];
    }
    try {
      const parsed = parseAllReferences(normalizedText);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.warn('Error parsing references:', error);
      return [{ type: 'text' as const, content: normalizedText }];
    }
  }, [normalizedText]);

  if (!normalizedText || normalizedText.length === 0) {
    return null;
  }

  // If no references found, render plain text for better performance
  if (segments.length === 1 && segments[0]?.type === 'text') {
    return <Text style={textStyle}>{normalizedText}</Text>;
  }

  return (
    <View style={styles.container}>
      {segments.map((segment, index) => {
        if (segment.type === 'reference' && segment.reference) {
          return (
            <DataReferenceView
              key={`ref-${segment.reference.id}-${index}`}
              type={segment.reference.type}
              title={segment.reference.title}
              id={segment.reference.id}
              theme={theme}
              onPress={onReferencePress ? () => onReferencePress(segment.reference!) : undefined}
            />
          );
        }
        return (
          <Text key={`text-${index}`} style={textStyle}>
            {segment.content}
          </Text>
        );
      })}
    </View>
  );
});

ParsedTextWithReferences.displayName = 'ParsedTextWithReferences';

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-start', // Align to top for better text wrapping
    flex: 1, // Take available space but respect parent constraints
    maxWidth: '100%', // Ensure it doesn't exceed parent width
  },
});

export default ParsedTextWithReferences;

